// parseExport.ts — browser-side reader for a raw AI data export. Turns a
// ChatGPT or Claude export (a `.zip`, or the bare `conversations.json`) into the
// `ImportedSource[]` shape the server-side Self Importer already understands, so
// the whole unzip/parse step happens on the user's machine and only distilled
// conversation text crosses the wire (never the full export). The server route
// then runs the same `runImport` LLM over these sources as the paste lane.
//
// We bias hard toward the USER's own turns — personal memory lives in what the
// user said, not what the assistant replied — and cap aggressively so one export
// can't blow past the importer's token budget.

import { personBlock } from '../memory/voices'

export interface ImportedSource {
  path: string
  label: string
  content: string
  size: number
  kind: string
  /** the conversation's own day (ISO), so what it says is dated then, not on the day it was imported (158) */
  dated?: string | null
}

export interface ParsedExport {
  sources: ImportedSource[]
  /** total conversations found in the file (before the cap) */
  found: number
  /** how many we actually kept after the recency + size cap */
  kept: number
  kind: 'chatgpt' | 'claude' | 'generic'
}

// caps — keep the whole import comfortably inside the importer's context budget.
const MAX_CONVOS = 40
const MAX_CHARS_PER_CONVO = 3500
const MAX_TOTAL_CHARS = 120_000

interface Turn {
  role: 'user' | 'assistant'
  text: string
}
interface Convo {
  title: string
  when: number // epoch seconds, best-effort (0 if unknown)
  turns: Turn[]
}

// ── format detection + extraction ────────────────────────────────────────────

function toSeconds(v: unknown): number {
  if (typeof v === 'number') return v > 1e12 ? v / 1000 : v
  if (typeof v === 'string') {
    const t = Date.parse(v)
    return Number.isNaN(t) ? 0 : t / 1000
  }
  return 0
}

// ChatGPT: array of { title, create_time, mapping: { id: { message } } }
function extractChatGpt(data: unknown[]): Convo[] {
  const out: Convo[] = []
  for (const raw of data) {
    const c = raw as Record<string, unknown>
    const mapping = c.mapping as Record<string, { message?: Record<string, unknown> }> | undefined
    if (!mapping) continue
    const nodes = Object.values(mapping)
      .map((n) => n?.message)
      .filter(Boolean) as Record<string, unknown>[]
    // order by the message create_time when present
    nodes.sort((a, b) => toSeconds(a.create_time) - toSeconds(b.create_time))
    const turns: Turn[] = []
    for (const m of nodes) {
      const author = m.author as { role?: string } | undefined
      const role = author?.role
      if (role !== 'user' && role !== 'assistant') continue
      const content = m.content as { parts?: unknown[] } | undefined
      const parts = Array.isArray(content?.parts) ? content!.parts : []
      const text = parts.filter((p) => typeof p === 'string').join('\n').trim()
      if (text) turns.push({ role, text })
    }
    if (turns.length) {
      out.push({ title: String(c.title ?? 'conversation'), when: toSeconds(c.create_time), turns })
    }
  }
  return out
}

// Claude: array of { name, created_at, chat_messages: [{ sender, text }] }
function extractClaude(data: unknown[]): Convo[] {
  const out: Convo[] = []
  for (const raw of data) {
    const c = raw as Record<string, unknown>
    const msgs = c.chat_messages as Record<string, unknown>[] | undefined
    if (!Array.isArray(msgs)) continue
    const turns: Turn[] = []
    for (const m of msgs) {
      const sender = m.sender === 'human' ? 'user' : m.sender === 'assistant' ? 'assistant' : null
      if (!sender) continue
      let text = typeof m.text === 'string' ? m.text : ''
      // newer Claude exports nest text under content[].text
      if (!text && Array.isArray(m.content)) {
        text = (m.content as { text?: string }[])
          .map((b) => (typeof b?.text === 'string' ? b.text : ''))
          .join('\n')
      }
      text = text.trim()
      if (text) turns.push({ role: sender, text })
    }
    if (turns.length) {
      out.push({ title: String(c.name ?? 'conversation'), when: toSeconds(c.created_at), turns })
    }
  }
  return out
}

function detectAndExtract(data: unknown): { kind: ParsedExport['kind']; convos: Convo[] } {
  if (!Array.isArray(data)) return { kind: 'generic', convos: [] }
  const first = data[0] as Record<string, unknown> | undefined
  if (first && 'mapping' in first) return { kind: 'chatgpt', convos: extractChatGpt(data) }
  if (first && 'chat_messages' in first) return { kind: 'claude', convos: extractClaude(data) }
  return { kind: 'generic', convos: [] }
}

// ── render a conversation into a compact, user-weighted transcript ────────────

function renderConvo(c: Convo): string {
  const lines: string[] = []
  for (const t of c.turns) {
    // keep the user verbatim, inside a person block (lib/memory/voices): only
    // their turns are their words. Clip assistant turns hard (context only).
    if (t.role === 'user') lines.push(`User:\n${personBlock(t.text)}`)
    else lines.push(`Assistant: ${t.text.slice(0, 400)}`)
  }
  let text = lines.join('\n')
  if (text.length > MAX_CHARS_PER_CONVO) text = text.slice(0, MAX_CHARS_PER_CONVO) + '\n…'
  return text
}

// ── the entry point ───────────────────────────────────────────────────────────

async function readConversationsJson(file: File): Promise<unknown> {
  const name = file.name.toLowerCase()
  if (name.endsWith('.json')) {
    return JSON.parse(await file.text())
  }
  // treat everything else as a zip. Hand JSZip an ArrayBuffer (universally
  // supported) rather than the Blob/File, which JSZip can only read where a
  // FileReader global exists.
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(await file.arrayBuffer())
  // find a conversations.json anywhere in the archive, else the largest .json
  const entries = Object.values(zip.files).filter((f) => !f.dir)
  const convoEntry =
    entries.find((f) => /(^|\/)conversations\.json$/i.test(f.name)) ??
    entries
      .filter((f) => f.name.toLowerCase().endsWith('.json'))
      .sort((a, b) => b.name.length - a.name.length)[0]
  if (!convoEntry) throw new Error('no conversations.json found in this export')
  const raw = await convoEntry.async('string')
  return JSON.parse(raw)
}

/**
 * Parse a user-supplied export File into importer sources. Throws with a
 * human-readable message if the file isn't a recognizable export.
 */
export async function parseExportFile(file: File): Promise<ParsedExport> {
  let data: unknown
  try {
    data = await readConversationsJson(file)
  } catch (err) {
    throw new Error(
      /json/i.test((err as Error).message)
        ? "couldn't read this file — export the .zip or conversations.json from your AI tool"
        : (err as Error).message,
    )
  }

  const { kind, convos } = detectAndExtract(data)
  if (!convos.length) {
    throw new Error(
      "this doesn't look like a ChatGPT or Claude export — it should hold your conversations",
    )
  }

  // most recent first, then apply the caps.
  convos.sort((a, b) => b.when - a.when)
  const sources: ImportedSource[] = []
  let total = 0
  let kept = 0
  const label = kind === 'chatgpt' ? 'ChatGPT export' : kind === 'claude' ? 'Claude export' : 'export'
  for (const c of convos) {
    if (kept >= MAX_CONVOS || total >= MAX_TOTAL_CHARS) break
    const content = renderConvo(c)
    if (!content.trim()) continue
    total += content.length
    kept += 1
    sources.push({
      path: `export://${kind}/${kept}`,
      label: `${label}: ${c.title}`.slice(0, 120),
      content,
      size: content.length,
      kind: `${kind}-export`,
      ...(c.when > 0 ? { dated: new Date(c.when * 1000).toISOString() } : {}),
    })
  }

  return { sources, found: convos.length, kept, kind }
}
